import AddIcon from "@mui/icons-material/Add";
import PartiesIcon from "@mui/icons-material/People";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCase, useCaseSubmit } from "../../context/CaseContext";
import { GuideButton } from "../guide/GuideButton";
import { GuideMarkers } from "../guide/GuideContext";
import { claimHasClaimedAmount } from "../../shared/casePrerequisites";
import { Claim, Damage } from "../../shared/CaseModel";
import randomId from "../../shared/randomId";
import { leavesMissingWinChanceOf } from "../../shared/winChance";
import CenteredMessageBox from "../general/CenteredMessageBox";
import { ClaimedDamage } from "../QuantumEditorDialog";
import { ClaimCallbacks as CardClaimCallbacks, ClaimCard } from "./ClaimCard";
import { ClaimEditorDialog } from "./ClaimEditorDialog";
import { Claim as CardClaim, RowChances } from "./IssueModel";
import { convertFrom, convertTo } from "./IssueModelConverter";

export interface ClaimCallbacks {
  add: (claim: Claim) => void;
  // with a damage to save along, where an amount's damage is new or renamed
  update: (claim: Claim, damage?: Damage) => void;
  delete: (claim: Claim) => void;
}

/** Each damage of the case, with the claims that claim it, labelled. */
function claimedDamagesOf(
  damages: Damage[],
  claims: CardClaim[],
  label: (claim: CardClaim, index: number) => string,
): ClaimedDamage[] {
  return damages.map((damage) => ({
    damage,
    claimedIn: claims.flatMap((claim, index) =>
      claim.quantums?.some(({ damageId }) => damageId === damage.id)
        ? [{ claimId: claim.id, label: label(claim, index) }]
        : [],
    ),
  }));
}

export const Claims = () => {
  const { t } = useTranslation(undefined, { keyPrefix: "case.claims" });
  const caseContext = useCase();
  const caseId = caseContext.getCase().id;
  const { claims, damages, parties, settlements } = caseContext.getCase();
  const navigate = useNavigate();
  const [newClaimEditorOpen, setNewClaimEditorOpen] = useState(false);
  const claimCanBeCreated = parties && parties.length > 0;
  const hasClaims = (claims ?? []).length > 0;

  const submit = useCaseSubmit();
  const claimCallbacks: ClaimCallbacks = {
    add: function (claim: Claim): void {
      submit({ claim: JSON.stringify(claim) }, { method: "post" });
    },
    update: function (claim: Claim, damage?: Damage): void {
      submit(
        {
          claim: JSON.stringify(claim),
          ...(damage && { damage: JSON.stringify(damage) }),
        },
        { method: "put" },
      );
    },
    delete: function (claim: Claim): void {
      submit({ claimId: claim.id }, { method: "delete" });
    },
  };

  // convert claims to format accepted by the card
  const cardClaims: CardClaim[] = useMemo(
    () =>
      claims
        ? claims.map((claim) => convertTo(claim, parties ?? [], settlements))
        : [],
    [claims, parties, settlements],
  );
  const claimedDamages = useMemo(
    () =>
      claimedDamagesOf(damages ?? [], cardClaims, ({ title }, index) =>
        t("reference", { ordinal: index + 1, title }),
      ),
    [damages, cardClaims, t],
  );

  // the calculation's chances of every claim, issue and group, and whether a
  // change is being calculated meanwhile
  const issueWinChances = caseContext.getCase().results?.issueWinChances;
  const chances: RowChances = {
    of: (rowId) => issueWinChances?.[rowId],
    calculating: caseContext.isSaving,
  };

  const callbacks: CardClaimCallbacks = {
    onClaimDelete: (claim) => claimCallbacks.delete(convertFrom(claim)),
    onClaimUpdate: (claim, damage) =>
      claimCallbacks.update(convertFrom(claim), damage),
  };

  return (
    <Stack spacing={2.5} sx={{ mb: 2 }}>
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 700, letterSpacing: "-.01em" }}
          >
            {t("title")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.5, maxWidth: "72ch" }}
          >
            {t("description")}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <GuideButton />
          {/* without claims the empty state below carries this button */}
          {hasClaims && claimCanBeCreated && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={caseContext.readOnly}
              // open claim editor
              onClick={() => setNewClaimEditorOpen(true)}
              title={t("tip.add")}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {t("add")}
            </Button>
          )}
        </Stack>
      </Stack>

      {!hasClaims ? (
        <CenteredMessageBox
          message={
            claimCanBeCreated ? t("noClaims") : t("noClaimsAndNoParties")
          }
          action={{
            label: claimCanBeCreated ? t("add") : t("goToParties"),
            icon: claimCanBeCreated ? <AddIcon /> : <PartiesIcon />,
            onClick: () =>
              claimCanBeCreated
                ? setNewClaimEditorOpen(true)
                : navigate(`/cases/${caseId}/configuration/parties`),
            // adding a claim changes the case, going to the parties does not
            disabled: claimCanBeCreated && caseContext.readOnly,
          }}
        />
      ) : (
        <>
          {cardClaims.map((claim, index) => (
            // the guide marks what the claims have in common on the first
            <GuideMarkers key={"claim_" + index} shown={index === 0}>
              <ClaimCard
                claim={claim}
                callbacks={callbacks}
                ordinal={`${index + 1}`}
                parties={parties || []}
                // counted off the stored claim with the same helper the gate
                // uses, so a collapsed card still shows the gap
                winChancesMissing={
                  claims?.[index]
                    ? leavesMissingWinChanceOf(claims[index]).length
                    : 0
                }
                // per claim, not per case: another claim carrying an amount
                // does not make this one worth anything
                claimedAmountMissing={
                  claims?.[index]
                    ? !claimHasClaimedAmount(claims[index])
                    : false
                }
                damages={claimedDamages}
                chances={chances}
              />
            </GuideMarkers>
          ))}
        </>
      )}

      {newClaimEditorOpen && (
        <ClaimEditorDialog
          onClose={(claim?) => {
            setNewClaimEditorOpen(false);
            if (claim) claimCallbacks.add({ ...claim, id: randomId() });
          }}
          open={newClaimEditorOpen}
          parties={parties}
        />
      )}
    </Stack>
  );
};
